import {Fieldset} from '@smart/react-components';
import type {PropsWithChildren} from 'react';
import {useCallback} from 'react';
import {useMemo} from 'react';
import {useRef} from 'react';
import {createContext} from 'react';
import {useEffect} from 'react';
import {useState} from 'react';
import React, {useContext} from 'react';
import type {ValidationError} from 'yup';
import type {Maybe} from '../../../graphql';
import {objEquals} from '../../common/helpers';
import {FieldContext} from './field-wrapper';
import Fields from './fields';
import type {
  ErrorType,
  FakeError,
  IWrapperProvider,
  JSONValue,
  WrapperHandler,
  allSchemas,
  preValidationTransform,
} from './types';

interface IProps {
  tag: string;
  outputTransform?: preValidationTransform;
  set?: boolean;
  setProps?: Partial<FieldsetProps>;
  hideLegend?: boolean;
  schema?: allSchemas;
  block?: boolean | 'messages';
  skipTag?: boolean;
  required?: boolean;
  label?: Maybe<string>;
}

export const InterceptContext = createContext<
  | {
      errors: FakeError | undefined;
      showErrors: IWrapperProvider[`showErrors`];
    }
  | undefined
>(undefined);

type FieldsetProps = React.ComponentProps<typeof Fieldset>;

const Intercept = function ({
  tag,
  outputTransform,
  schema,
  setProps,
  children,
  label,
  hideLegend = false,
  set = false,
  block = false,
  skipTag = false,
  required = false,
}: PropsWithChildren<IProps>) {
  const field = useContext<IWrapperProvider>(FieldContext as any);
  const loaded = useRef(false);
  const [data, setData] = useState<{
    state: JSONValue;
    errors: ErrorType | undefined;
  }>();

  useEffect(() => {
    if (data?.state === undefined) return;

    if (!loaded.current && !!data) loaded.current = true;

    field.registerField(
      tag,
      {
        validateSync: (value: any) => {
          const errorResult: FakeError = {
            internals: undefined,
            self: undefined,
          };
          try {
            schema?.validateSync(value);
          } catch (err) {
            errorResult.self = err as ValidationError;
          }
          if (data.errors && Object.entries(data.errors).length) {
            errorResult.internals = data.errors;
          }
          if (!errorResult.internals && !errorResult.self) return;
          throw errorResult;
        },
      } as unknown as allSchemas,
      false,
      data.state,
    );

    field.genericHandler(tag, data.state);
  }, [data, tag]);

  useEffect(() => {
    return () => {
      field.registerField(tag, undefined, true, null);
    };
  }, []);

  const changeHandler: WrapperHandler = useCallback(
    (childState, childErrors) => {
      setData((prevData) => {
        const newState = outputTransform
          ? outputTransform(childState)
          : childState;
        const newErrors = childErrors;

        const stateChanged = !objEquals(prevData?.state, newState);
        const errorChanged = !objEquals(prevData?.errors, newErrors);

        if (stateChanged || errorChanged) {
          return {
            state: stateChanged ? newState : childState,
            errors: errorChanged ? newErrors : childErrors,
          };
        }

        return prevData;
      });
    },
    [outputTransform, objEquals, setData],
  );

  const newTag = skipTag
    ? field.translationPrefix
    : `${field.translationPrefix}.${tag}`;
  const errors = field.errors?.internals && field.errors.internals[tag];

  const message = useMemo(
    () =>
      field.showErrors === true && errors?.self
        ? {id: ``, text: errors.self.message}
        : undefined,
    [errors, field.showErrors],
  );

  const newShowErrors = !field.showErrors
    ? false
    : block === `messages`
      ? `invalid`
      : block === true
        ? false
        : true;

  return (
    <InterceptContext.Provider
      value={{
        errors,
        showErrors: newShowErrors,
      }}
    >
      <Fields.Wrapper
        translationPrefix={newTag}
        onUpdate={changeHandler}
        showErrors={field.showErrors}
        disabled={field.disabled}
      >
        {set ? (
          <Fieldset
            label={hideLegend ? `` : field.getLabel(tag, required, label)}
            invalid={!!field.showErrors && !!message}
            error={message?.text}
            // disabled={field.disabled} TODO: If needed, update the component with this property
            {...setProps}
          >
            {children}
          </Fieldset>
        ) : (
          children
        )}
      </Fields.Wrapper>
    </InterceptContext.Provider>
  );
};

export default Intercept;
